import { addCivilDays, buildHolidayMap, isBusinessDay, toCivilDate } from '../day-helpers.js';
import { buildObservedHolidays, classifyDay } from '../metadata-helpers.js';
import type {
  Holiday,
  ResolveProceduralDateInput,
  ResolveProceduralDateResult,
  SkippedDate,
} from '../types.js';

const SHORT_PERIOD_THRESHOLD = 5;

/**
 * Inferences this handler can't make from a bare (start, days) pair, surfaced
 * as warnings so the user can verify the operative date.
 */
const FORWARD_PERIOD_SHAPE_WARNING =
  'Both the start day and the result day are excluded, treating the period as ending at an event (CPR 2.8(3)). For a "within X days" deadline, only the start day is excluded and the deadline is the final counted day, one day earlier than shown.';
const ROLL_FORWARD_SCOPE_WARNING =
  'The deadline landed on a non-business day and was rolled forward (CPR 2.8(5)). That rule covers acts done at the court office, such as filing. A deadline for serving another party may not move, so verify before relying on the rolled date.';
const ROLL_BACKWARD_WARNING =
  'The result landed on a non-business day and was moved back to the previous business day as a precaution. CPR 2.8(5) does not strictly govern backward-counted periods, so verify the operative date.';

/**
 * UK E&W "clear days" per CPR 2.8: both the anchor day and the result day are
 * excluded — only the days strictly between them count.
 *  - Short periods (≤ 5 days, CPR 2.8(4)): weekends and bank holidays don't
 *    count toward the period.
 *  - Long periods (> 5 days): every intervening day counts.
 *  - If the resulting day lands on a weekend or holiday, it rolls forward to
 *    the next business day (CPR 2.8(5)).
 * Only `days` is honoured; `years` / `months` are ignored with a warning.
 */
export function resolveUkEwClearCpr(
  input: ResolveProceduralDateInput,
): ResolveProceduralDateResult {
  const start = toCivilDate(input.startDate);
  const { years = 0, months = 0, days = 0 } = input.interval;
  const holidayMap = buildHolidayMap(input.holidays);

  const warnings: string[] = [];
  if (years !== 0 || months !== 0) {
    warnings.push('Years/months on the interval are ignored for UK E&W clear-day counting.');
  }

  if (days === 0) {
    return shape(start, start, [], [], false, holidayMap, warnings);
  }

  const step = days > 0 ? 1 : -1;
  const shortPeriod = Math.abs(days) <= SHORT_PERIOD_THRESHOLD;
  const { lastClearDay, countedDates, skippedDates } = countClearDays(
    start,
    days,
    step,
    shortPeriod,
    holidayMap,
  );

  if (days > 0) warnings.push(FORWARD_PERIOD_SHAPE_WARNING);

  const naiveResult = addCivilDays(lastClearDay, step);
  let resultDate = naiveResult;
  while (!isBusinessDay(resultDate, holidayMap)) {
    const { skip } = classifyDay(resultDate, holidayMap);
    if (skip !== undefined) skippedDates.push(skip);
    resultDate = addCivilDays(resultDate, step);
  }
  const endDateRolledForward = resultDate.getTime() !== naiveResult.getTime();
  if (endDateRolledForward) {
    warnings.push(step > 0 ? ROLL_FORWARD_SCOPE_WARNING : ROLL_BACKWARD_WARNING);
  }

  return shape(
    start,
    resultDate,
    countedDates,
    skippedDates,
    endDateRolledForward,
    holidayMap,
    warnings,
  );
}

function countClearDays(
  start: Date,
  days: number,
  step: number,
  shortPeriod: boolean,
  holidayMap: Map<string, Holiday>,
): { lastClearDay: Date; countedDates: Date[]; skippedDates: SkippedDate[] } {
  const target = Math.abs(days);
  const countedDates: Date[] = [];
  const skippedDates: SkippedDate[] = [];
  let cursor = addCivilDays(start, step);
  let counted = 0;
  while (counted < target) {
    if (shortPeriod) {
      const { isCounting, skip } = classifyDay(cursor, holidayMap);
      if (isCounting) {
        countedDates.push(cursor);
        counted += 1;
      } else if (skip !== undefined) {
        skippedDates.push(skip);
      }
    } else {
      countedDates.push(cursor);
      counted += 1;
    }
    if (counted < target) cursor = addCivilDays(cursor, step);
  }
  return { lastClearDay: cursor, countedDates, skippedDates };
}

function shape(
  start: Date,
  resultDate: Date,
  countedDates: Date[],
  skippedDates: SkippedDate[],
  endDateRolledForward: boolean,
  holidayMap: Map<string, Holiday>,
  warnings: string[],
): ResolveProceduralDateResult {
  const target = Math.abs(countedDates.length);
  const shortPeriod = target <= SHORT_PERIOD_THRESHOLD;

  const windowStart = resultDate.getTime() < start.getTime() ? resultDate : start;
  const windowEnd = resultDate.getTime() < start.getTime() ? start : resultDate;

  return {
    resultDate,
    jurisdiction: 'uk-ew',
    dayCount: 'clear-cpr',
    direction:
      resultDate.getTime() === start.getTime()
        ? 'same-day'
        : resultDate.getTime() > start.getTime()
          ? 'after'
          : 'before',
    startDateExcluded: true,
    endDateRolledForward,
    weekendsCount: !shortPeriod,
    publicHolidaysCount: !shortPeriod,
    countedDates,
    skippedDates,
    observedHolidays: buildObservedHolidays(holidayMap, windowStart, windowEnd),
    windowStart,
    windowEnd,
    warnings,
  };
}
